// Dialog behavior for modal panels: focus management (trap + restore),
// Escape-to-close, and initial focus. Apply to the panel element of any
// modal overlay.
//
// Nested-popper caveat: poppers that add their own document Escape listeners
// (TerminalContextMenu, HostPicker, the sidebar dropdown) don't currently
// render inside a Modal. If one ever does, it must stopPropagation() on
// Escape or both layers will close at once.

import { useEffect, type RefObject } from 'react';

const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useDialog(
    panelRef: RefObject<HTMLElement | null>,
    onClose: () => void,
): void {
    // Initial focus + focus restore. Runs once per mount; if a child already
    // claimed focus via autoFocus (e.g. a confirm input), leave it be.
    useEffect(() => {
        const invoker = document.activeElement as HTMLElement | null;
        const panel = panelRef.current;
        if (panel && !panel.contains(document.activeElement)) {
            panel.querySelector<HTMLElement>(FOCUSABLE)?.focus();
        }
        return () => {
            // Restoring to xterm's hidden textarea after a terminal-triggered
            // dialog is intentional — typing resumes in the terminal.
            if (invoker?.isConnected) invoker.focus();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Escape closes; Tab/Shift+Tab wrap within the panel.
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
                return;
            }
            if (e.key !== 'Tab') return;
            const panel = panelRef.current;
            if (!panel) return;
            const focusables = Array.from(
                panel.querySelectorAll<HTMLElement>(FOCUSABLE),
            );
            if (focusables.length === 0) return;
            const first = focusables[0]!;
            const last = focusables[focusables.length - 1]!;
            const current = document.activeElement;
            if (e.shiftKey) {
                if (current === first || !panel.contains(current)) {
                    e.preventDefault();
                    last.focus();
                }
            } else if (current === last || !panel.contains(current)) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [panelRef, onClose]);
}
